import React, { useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useApp } from "../context/AppContext";
import Seo from "../components/Seo";
import { FaqSection } from "../components/FaqSection";
import { faqData } from "../data/faqData";
import { labelTemplates, IMAGES, PRICE_PER_BOTTLE, MIN_BOTTLES } from "../data/mock";
import { createCuvee } from "../services/api";
import { Upload, X, Check, ShoppingBag } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const FONTS = [
  { label: "Playfair", value: '"Playfair Display", serif' },
  { label: "Inter", value: "Inter, sans-serif" },
  { label: "Script", value: '"Great Vibes", cursive' },
];

const LabelDesigner = () => {
  const { t, lang, addToCart } = useApp();
  const { toast } = useToast();
  const fileRef = useRef(null);

  const [blend, setBlend] = useState(null);
  const [tpl, setTpl] = useState(labelTemplates[0]);
  const [name, setName] = useState("");
  const [line2, setLine2] = useState("");
  const [producer, setProducer] = useState("");
  const [image, setImage] = useState(null);
  const [font, setFont] = useState(labelTemplates[0].font);
  const [textColor, setTextColor] = useState(labelTemplates[0].text);
  const [accent, setAccent] = useState(labelTemplates[0].accent);

  useEffect(() => {
    try {
      const b = JSON.parse(localStorage.getItem("moc_blend") || "null");
      if (b) { setBlend(b); setName(b.name || ""); }
    } catch { /* ignore */ }
  }, []);

  const applyTemplate = (template) => {
    setTpl(template);
    setFont(template.font);
    setTextColor(template.text);
    setAccent(template.accent);
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(file);
  };

  const bottles = blend?.bottles || MIN_BOTTLES;
  const price = bottles * PRICE_PER_BOTTLE;

  const addOrder = async () => {
    const label = { template: tpl.id, name: name || t("label.placeholderName"), line2, producer, font, textColor, accent, hasImage: !!image };
    try {
      await createCuvee({ name: name || t("label.placeholderName"), selected: blend?.selected || [], profile: blend?.profile || {}, bottles, label });
    } catch { /* non-blocking */ }
    addToCart({
      id: `cuvee-${Date.now()}`,
      name: (name || t("label.placeholderName")) + (blend ? "" : " — Custom Label"),
      price,
      img: image || IMAGES.bottles1,
      bottles,
      custom: true,
    });
    toast({ title: t("common.added"), description: name || t("label.placeholderName") });
  };

  const dName = name || t("label.placeholderName");
  const dLine2 = line2 || t("label.placeholderLine2");
  const dProducer = producer || t("label.placeholderProducer");

  return (
    <div className="pt-20 min-h-screen bg-cream">
      <Seo title={t("label.title")} description={t("label.sub")} image={IMAGES.label2} path="/label-designer" lang={lang} />
      <div className="relative py-16 overflow-hidden bg-ink">
        <img src={IMAGES.label2} alt="Weinflaschen mit individuellen Etiketten" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <h1 className="font-serif text-4xl md:text-5xl text-cream">{t("label.title")}</h1>
          <p className="mt-4 text-cream/75 max-w-2xl mx-auto">{t("label.sub")}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-14 grid lg:grid-cols-[1fr_440px] gap-12">
        {/* CONTROLS */}
        <div className="space-y-8">
          {/* templates */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-ink/50 mb-4">{t("label.template")}</h3>
            <div className="flex flex-wrap gap-3">
              {labelTemplates.map((template) => (
                <button key={template.id} onClick={() => applyTemplate(template)}
                  className={`relative w-24 h-16 rounded-xl border-2 overflow-hidden transition-all ${tpl.id === template.id ? "border-wine shadow-md scale-105" : "border-border hover:border-wine/40"}`}
                  style={{ background: template.bg }}>
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-medium" style={{ color: template.text, fontFamily: template.font }}>{template.name[lang]}</span>
                  {tpl.id === template.id && <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-wine flex items-center justify-center"><Check className="w-2.5 h-2.5 text-white" /></span>}
                </button>
              ))}
            </div>
          </div>

          {/* text fields */}
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <Label className="text-ink/70">{t("label.wineName")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("label.placeholderName")} className="mt-2 bg-white" maxLength={28} />
            </div>
            <div>
              <Label className="text-ink/70">{t("label.line2")}</Label>
              <Input value={line2} onChange={(e) => setLine2(e.target.value)} placeholder={t("label.placeholderLine2")} className="mt-2 bg-white" maxLength={24} />
            </div>
            <div>
              <Label className="text-ink/70">{t("label.producer")}</Label>
              <Input value={producer} onChange={(e) => setProducer(e.target.value)} placeholder={t("label.placeholderProducer")} className="mt-2 bg-white" maxLength={24} />
            </div>
          </div>

          {/* upload */}
          <div>
            <Label className="text-ink/70">{t("label.upload")}</Label>
            <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" />
            {image ? (
              <div className="mt-2 flex items-center gap-4 bg-white border border-border rounded-xl p-4">
                <img src={image} alt="logo" className="w-14 h-14 object-contain rounded" />
                <button onClick={() => setImage(null)} className="flex items-center gap-1.5 text-sm text-destructive hover:underline"><X className="w-4 h-4" /> {t("label.removeImg")}</button>
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()} className="mt-2 w-full border-2 border-dashed border-wine/30 rounded-xl p-7 flex flex-col items-center gap-2 text-ink/50 hover:border-wine hover:bg-white transition-all">
                <Upload className="w-7 h-7 text-wine/60" />
                <span className="text-sm font-medium">{t("label.upload")}</span>
                <span className="text-xs text-ink/40">{t("label.uploadHint")}</span>
              </button>
            )}
          </div>

          {/* font + colors */}
          <div className="grid sm:grid-cols-3 gap-5">
            <div>
              <Label className="text-ink/70">{t("label.font")}</Label>
              <div className="flex gap-2 mt-2">
                {FONTS.map((f) => (
                  <button key={f.value} onClick={() => setFont(f.value)} className={`flex-1 py-2 rounded-lg border text-sm transition-all ${font === f.value ? "border-wine bg-wine/5 text-wine" : "border-border bg-white text-ink/60"}`} style={{ fontFamily: f.value }}>{f.label}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-ink/70">{t("label.textColor")}</Label>
              <div className="mt-2 flex items-center gap-2 bg-white border border-border rounded-lg px-3 h-10">
                <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent" />
                <span className="text-sm text-ink/60 uppercase">{textColor}</span>
              </div>
            </div>
            <div>
              <Label className="text-ink/70">{t("label.accentColor")}</Label>
              <div className="mt-2 flex items-center gap-2 bg-white border border-border rounded-lg px-3 h-10">
                <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent" />
                <span className="text-sm text-ink/60 uppercase">{accent}</span>
              </div>
            </div>
          </div>
        </div>

        {/* LIVE PREVIEW */}
        <div className="lg:sticky lg:top-24 self-start">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-ink/50 mb-4 text-center">{t("label.preview")}</h3>
          {/* Realistic Bordeaux bottle */}
          <div className="relative mx-auto" style={{ width: 200, height: 600 }}>
            <svg viewBox="0 0 200 600" width="200" height="600" className="absolute inset-0" style={{ filter: "drop-shadow(0 26px 36px rgba(43,27,29,0.4))" }}>
              <defs>
                <linearGradient id="glass" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#2c0a11" />
                  <stop offset="22%" stopColor="#641b29" />
                  <stop offset="40%" stopColor="#8d2c3e" />
                  <stop offset="50%" stopColor="#a8455a" />
                  <stop offset="60%" stopColor="#8d2c3e" />
                  <stop offset="80%" stopColor="#5a1623" />
                  <stop offset="100%" stopColor="#240810" />
                </linearGradient>
              </defs>
              <path d="M82 6 L118 6 L118 24 L116 30 L122 122 C122 178 172 184 172 240 L172 580 Q172 590 162 590 L38 590 Q28 590 28 580 L28 240 C28 184 78 178 78 122 L84 30 L82 24 Z" fill="url(#glass)" />
              {/* vertical glossy highlights */}
              <rect x="58" y="280" width="9" height="290" rx="4.5" fill="rgba(255,255,255,0.16)" />
              <rect x="132" y="280" width="4" height="265" rx="2" fill="rgba(255,255,255,0.06)" />
            </svg>
            {/* capsule / foil on neck */}
            <div className="absolute" style={{ left: "50%", transform: "translateX(-50%)", top: 6, width: 34, height: 58 }}>
              <div className="w-full h-full rounded-t-sm" style={{ background: "linear-gradient(90deg, #160709 0%, #3a2024 48%, #160709 100%)" }} />
              <div className="absolute bottom-0 inset-x-0" style={{ height: 4, background: accent }} />
            </div>
            {/* label */}
            <div className="absolute rounded-sm shadow-xl flex flex-col items-center justify-between text-center" style={{ top: "58%", bottom: "9%", left: "28%", right: "28%", padding: "14px 10px", background: tpl.bg, color: textColor }}>
              <div className="w-full">
                <div className="w-9 h-0.5 mx-auto mb-2" style={{ background: accent }} />
                {image && <img src={image} alt="Hochgeladenes Logo für das Etikett" className="w-11 h-11 object-contain mx-auto mb-1.5" />}
              </div>
              <div className="flex-1 flex flex-col items-center justify-center w-full">
                <p className="text-[9px] uppercase tracking-[0.28em] mb-1" style={{ color: accent }}>{dProducer}</p>
                <p className="leading-tight break-words" style={{ fontFamily: font, fontSize: font.includes("Vibes") ? "1.7rem" : "1.15rem", lineHeight: 1.1 }}>{dName}</p>
                <p className="text-[10px] mt-1 opacity-80" style={{ fontFamily: font.includes("Vibes") ? "Inter, sans-serif" : font }}>{dLine2}</p>
              </div>
              <div className="w-full">
                <div className="w-9 h-0.5 mx-auto mt-2" style={{ background: accent }} />
                <p className="text-[8px] uppercase tracking-widest mt-1.5 opacity-60">MYOWNCUVEE · 75cl</p>
              </div>
            </div>
          </div>

          {/* order box */}
          <div className="mt-8 bg-white rounded-2xl border border-border p-6">
            {blend && (
              <p className="text-sm text-ink/60 mb-3">{bottles} {t("common.bottles")} · {blend.name}</p>
            )}
            <div className="flex items-center justify-between mb-4">
              <span className="text-ink/60 text-sm">{t("common.total")}</span>
              <span className="font-serif text-3xl text-wine">{t("common.currency")} {price.toFixed(2)}</span>
            </div>
            <Button onClick={addOrder} className="w-full h-12 bg-wine hover:bg-wine-dark text-white rounded-full gap-2">
              <ShoppingBag className="w-5 h-5" /> {t("label.addToOrder")}
            </Button>
          </div>
        </div>
      </div>
      <FaqSection faqs={faqData.label[lang] || faqData.label.de} lang={lang} />
    </div>
  );
};

export default LabelDesigner;
